import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Settings, Trash2, AlertTriangle } from 'lucide-react';
import { storage } from '@/lib/storage';
import apiClient from '@/services/apiClient';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState, useEffect } from 'react';
import PackageFeatureGate from '@/components/PackageFeatureGate';
import { billingService, clubService } from '@/services/storageService';
import { applyClubTheme, type ClubThemeSettings } from '@/lib/theme';
import { useI18n } from '@/contexts/I18nContext';

export default function SettingsPage() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const { club, activeClubId } = useClub();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clubId = activeClubId ? Number(activeClubId) : null;
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });
  const resolvedPackageCode = String(
    (billingQuery.data as any)?.package || (billingQuery.data as any)?.recommended_package || 'package1',
  )
    .trim()
    .toLowerCase();
  const isThemeAppLocked = resolvedPackageCode !== 'package3';
  const themeQuery = useQuery({
    queryKey: ['club-theme', clubId],
    queryFn: () => clubService.getTheme(clubId as number),
    enabled: Boolean(clubId),
    retry: false,
    refetchOnWindowFocus: false,
  });


  const handleExportData = () => {
    (async () => {
      try {
        const [clubRes, membersRes, matchesRes, queueRes, courtsRes] = await Promise.all([
          apiClient.request('/club'),
          apiClient.request('/members'),
          apiClient.request('/matches'),
          apiClient.request('/queue'),
          apiClient.request('/courts'),
        ]);

        const data = {
          club: clubRes,
          members: membersRes,
          matches: matchesRes,
          queue: queueRes,
          courts: courtsRes,
          exportedAt: new Date().toISOString(),
        };

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${club?.name || 'club'}-backup-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(t('settings_export_success', 'Data exported successfully!'));
      } catch (err: any) {
        toast.error(`${t('settings_export_failed', 'Failed to export data')}: ${err.message || String(err)}`);
      }
    })();
  };

  return (
    <div className="relative min-h-[calc(100vh-8rem)]">
      <div
        className={`w-full space-y-6 animate-fade-in px-3 sm:px-4 lg:px-6 overflow-x-hidden transition-all ${
          isThemeAppLocked ? 'blur-[6px] pointer-events-none select-none' : ''
        }`}
      >
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Settings className="text-primary" />
            {t('settings_title', 'Settings')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('settings_manage_subtitle', 'Manage application settings')}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl md:text-3xl">{t('settings_theme_app_title', 'Theme & App Settings')}</CardTitle>
            <CardDescription className="text-base md:text-lg">
              {t('settings_theme_app_desc', 'Customize menu appearance, theme mode, font and language')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isThemeAppLocked && clubId ? (
              <ThemeSettings
                clubId={clubId}
                themeData={(themeQuery.data as ClubThemeSettings | null) || null}
                onSaved={async () => {
                  await queryClient.invalidateQueries({ queryKey: ['club-theme', clubId] });
                }}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>

      {isThemeAppLocked && (
        <div className="absolute inset-0 z-20 bg-background/30 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6">
          <PackageFeatureGate
            featureName={t('settings_theme_app_title', 'Theme & App Settings')}
            currentPackageCode={resolvedPackageCode}
            availabilityText="This feature is available on Elite (Package 3) only."
            onUpgrade={() => navigate('/dashboard/club')}
          />
        </div>
      )}
    </div>
  );
}

function ThemeSettings({
  clubId,
  themeData,
  onSaved,
}: {
  clubId: number;
  themeData: ClubThemeSettings | null;
  onSaved?: () => Promise<void> | void;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const [menuBg, setMenuBg] = useState<string>('');
  const [menuFont, setMenuFont] = useState<string>('');
  const [appFont, setAppFont] = useState<string>('');
  const isHexColor = (value: string) => /^#[0-9a-fA-F]{6}$/.test((value || '').trim());
  const menuBgInputValue = isHexColor(menuBg) ? menuBg : '#0f172a';

  const menuFontOptions = [
    'Inter, ui-sans-serif, system-ui',
    'Roboto, system-ui',
    '"Segoe UI", system-ui',
    '"Source Sans Pro", system-ui',
    '"Work Sans", system-ui',
    '"Nunito", system-ui',
  ];

  const appFontOptions = [
    'Inter, ui-sans-serif, system-ui',
    'Roboto, system-ui',
    '"Segoe UI", system-ui',
    '"Source Sans Pro", system-ui',
    '"Work Sans", system-ui',
    '"Nunito", system-ui',
    '"Merriweather", serif',
    '"Georgia", serif',
  ];

  useEffect(() => {
    setMode(String(themeData?.mode || 'light').toLowerCase() === 'dark' ? 'dark' : 'light');
    setMenuBg((themeData?.menu_bg_color || '').trim());
    setMenuFont((themeData?.menu_font || '').trim());
    setAppFont((themeData?.app_font || '').trim());
  }, [themeData]);

  const apply = () => {
    const obj: ClubThemeSettings = {
      mode,
      menu_bg_color: menuBg || null,
      menu_font: menuFont || null,
      app_font: appFont || null,
    };
    (async () => {
      try {
        await clubService.updateTheme(clubId, obj);
        applyClubTheme(obj);
        if (onSaved) await onSaved();
        toast.success(t('settings_saved', 'Theme settings applied for this club.'));
      } catch (err: any) {
        toast.error(err?.message || t('settings_save_failed', 'Failed to save club theme settings'));
      }
    })();
  };



  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
        <div>
          <Label className="text-base md:text-lg">{t('settings_theme_mode', 'Theme Mode')}</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as 'light' | 'dark')}>
            <SelectTrigger className="h-11 md:h-12 text-base md:text-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light" className="text-base md:text-lg">{t('settings_light', 'Light')}</SelectItem>
              <SelectItem value="dark" className="text-base md:text-lg">{t('settings_dark', 'Dark')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-base md:text-lg">{t('settings_menu_bg', 'Menu Background Color')}</Label>
          <Input
            type="color"
            value={menuBgInputValue}
            onChange={(e) => setMenuBg(e.target.value)}
            className="h-11 md:h-12"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-base md:text-lg">{t('settings_menu_font', 'Menu Font')}</Label>
          <Select value={menuFont} onValueChange={(v) => setMenuFont(v)}>
            <SelectTrigger className="h-11 md:h-12 text-base md:text-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {menuFontOptions.map((opt) => (
                <SelectItem key={opt} value={opt} className="text-base md:text-lg">
                  {opt.split(',')[0].replace(/\"/g, '')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-base md:text-lg">{t('settings_app_font', 'App Font')}</Label>
          <Select value={appFont} onValueChange={(v) => setAppFont(v)}>
            <SelectTrigger className="h-11 md:h-12 text-base md:text-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {appFontOptions.map((opt) => (
                <SelectItem key={opt} value={opt} className="text-base md:text-lg">
                  {opt.split(',')[0].replace(/\"/g, '')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={apply} className="h-11 md:h-12 text-base md:text-lg px-5">
          {t('common_save_apply', 'Save & Apply')}
        </Button>
      </div>
    </div>
  );
}
